import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { formatOneDecimal } from '../../lib/formatDecimal'

type RatingProps = {
  /** `businesses.rating_avg` — 0 when nobody has reviewed yet. */
  value: number
  /** `businesses.rating_count`. */
  count: number
  size?: 'sm' | 'md'
  className?: string
}

const STARS = [1, 2, 3, 4, 5] as const

/**
 * Rating summary. Five marks plus the number, because the number alone is hard
 * to scan in a list and the stars alone lose the precision.
 *
 * A business with no reviews does NOT render zero stars — an empty row of marks
 * reads as "rated badly" rather than "not yet rated", which is unfair to a
 * business that only registered last week and is exactly the kind of thing a
 * marketplace gets complained about.
 */
export const Rating = ({ value, count, size = 'md', className }: RatingProps) => {
  const { t, i18n } = useTranslation()
  const starSize = size === 'sm' ? 'size-3.5' : 'size-4'

  if (count === 0) {
    return (
      <span className={cn('type-body-sm text-muted-foreground', className)}>
        {t('business.rating.none')}
      </span>
    )
  }

  const rounded = Math.round(value * 2) / 2

  return (
    <span
      className={cn('inline-flex items-center gap-1.5', className)}
      aria-label={t('business.rating.ariaLabel', {
        rating: formatOneDecimal(value, i18n.language),
        count,
      })}
    >
      <span aria-hidden="true" className="inline-flex items-center gap-0.5">
        {STARS.map((star) => (
          <Star
            key={star}
            className={cn(
              starSize,
              star <= rounded
                ? 'fill-accent text-accent'
                : star - 0.5 === rounded
                  ? 'fill-accent/40 text-accent'
                  : 'fill-transparent text-border',
            )}
          />
        ))}
      </span>
      <span aria-hidden="true" className="type-body-sm font-semibold text-foreground">
        {formatOneDecimal(value, i18n.language)}
      </span>
      <span aria-hidden="true" className="type-body-sm text-muted-foreground">
        {t('business.rating.count', { count })}
      </span>
    </span>
  )
}
